import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useLocation } from 'react-router-dom';
import {
  Column,
  Grouping,
  GroupPanel,
  Pager,
  Paging,
  SearchPanel,
  Sorting,
  Selection,
  FilterPanel,
  FilterBuilder,
  FilterBuilderPopup,
  HeaderFilter,
  ColumnFixing,
  Export,
  Editing,
  CustomRule,
} from 'devextreme-react/data-grid';
import { Template } from 'devextreme-react/core/template';
import CustomStore from 'devextreme/data/custom_store';
import Query from 'devextreme/data/query';

import { withStyles } from '@material-ui/core/styles';

import { format as dateFormat } from 'date-fns';

import isPlainObject from 'lodash/isPlainObject';

import { SearchPropMasterWithLocation } from 'api/revue/import';
import { updateProspect } from 'api/prospector/prospects';
import { getWithToken, postWithToken } from 'api/network';
import StatusChip from 'components/explore/prospector/layout/StatusChip';
import { BoolIcon } from 'components/layout';
import { dataGridExtension } from 'components/devExtreme';
import CustomColumnChooser from 'components/customColumnChooser';
import DataGrid from 'components/devExtreme/DataGrid';
import { Constants } from 'utilities/constants'; 
import { toCurrency } from 'utilities/format';

const styles = theme => ({
  informer: {
    display: 'flex',
    flexFlow: 'column',
    justifyContent: 'center',
    width: '130px',
    background: '#80a4c7',
    color: '#fff',
    textAlign: 'center'
  },
  exclusionInformer: {
    display: 'flex',
    flexFlow: 'column',
    justifyContent: 'center',
    width: '130px',
    background: '#67c12e',
    color: '#fff',
    textAlign: 'center'
  },
  countLabel: {
    fontSize: '18px',
    paddingTop: '5px',
    margin: '0'
  },
  totalCountText: {
    paddingBottom: '5px',
  }
});

const validOpportunitySources = ['Aggregator', 'iBuyer', 'Auction', 'Broker', 'Homebuilder', 'Operator', 'Seller Direct', 'Wholesaler']
const validOpportunitySourcesFormatted = validOpportunitySources.join(',')

const columnSchema = [
  { width: 100, field: 'id', displayName: 'Prospect ID', type: 'number', allowEditing: false },
  { width: 90, field: 'asgPropID', displayName: 'PM ID', type: 'string', allowEditing: false },
  { width: 150, field: 'status', displayName: 'Prospect Status', type: 'string', allowEditing: false, alignment: 'center' },
  { width: 110, field: 'locationVerified', displayName: 'Location Verified', type: 'boolean', allowEditing: false },
  { width: 160, field: 'streetAddress', displayName: 'Street', type: 'string', allowEditing: false },
  { width: 110, field: 'city', displayName: 'City', type: 'string', allowEditing: false },
  { width: 75, field: 'state', displayName: 'State', type: 'string', allowEditing: false },
  { width: 75, field: 'zipCode', displayName: 'Zip Code', type: 'string', allowEditing: false },
  { width: 100, field: 'county', displayName: 'County', type: 'string', allowEditing: false },
  { width: 85, field: 'strategy', displayName: 'Strategy', type: 'string', allowEditing: true },
  {
    width: 90, field: 'listingPrice', displayName: 'Asking Price', type: 'number', allowEditing: true,
    format: { type: 'currency', precision: 0 },
    validation: {
      message: `Asking Price must be greater than or equal to ${toCurrency(Constants.UnderwriteMinAskingPrice, 0)} and less than $100,000,000`,
      rule: (e) => {
        if (e.value !== null && (e.value < Constants.UnderwriteMinAskingPrice || e.value >= 100000000)) {
          return false;
        } else {
          return true;
        }
      }
    }
  },
  { width: 120, field: 'inspectionPeriod', displayName: 'Inspection Period', type: 'boolean', allowEditing: true },
  {
    width: 75, field: 'beds', displayName: 'Beds', type: 'number', allowEditing: true,
    validation: {
      message: 'Beds must be an integer between 1 - 99',
      rule: (e) => {
        if (e.value !== null && (e.value <= 0 || e.value >= 100 || !Number.isInteger(e.value))) {
          return false;
        } else {
          return true;
        }
      }
    }
  },
  {
    width: 75, field: 'baths', displayName: 'Baths', type: 'number', allowEditing: true,
    format: { type: 'fixedPoint', precision: 1 },
    validation: {
      message: 'Baths must be greater than 0 and less than 100',
      rule: (e) => {
        if (e.value !== null && (e.value <= 0 || e.value >= 100)) {
          return false;
        } else {
          return true;
        }
      }
    }
  },
  {
    width: 75, field: 'yearBuilt', displayName: 'Year Built', type: 'number', allowEditing: true,
    validation: {
      message: `Year must be an integer between 1890 - ${new Date().getFullYear()}`,
      rule: (e) => {
        if (e.value !== null && (e.value < 1890 || e.value > new Date().getFullYear() || !Number.isInteger(e.value))) {
          return false;
        } else {
          return true;
        }
      }
    }
  },
  {
    width: 100, field: 'sqft', displayName: 'Square Feet', type: 'number', allowEditing: true,
    format: { type: 'fixedPoint', precision: 0 },
    validation: {
      message: 'Sq Ft must be an integer greater than 0 and less than 100,000,000',
      rule: (e) => {
        if (e.value !== null && (e.value <= 0 || e.value >= 100000000 || !Number.isInteger(e.value))) {
          return false;
        } else {
          return true;
        }
      }
    }
  },
  { width: 150, field: 'notes', displayName: 'Notes', type: 'string', allowEditing: true,
    validation: {
      message: 'Notes cannot be longer than 8,000 characters',
      rule: (e) => {
        if (e.value !== null && e.value.length > 8000) {
          return false;
        } else {
          return true;
        }
      }
    }
  },
  { width: 100, field: 'opportunitySource', displayName: 'Opportunity Source', type: 'string', allowEditing: true,
    validation: {
      message: `Opportunity Source does not equal any of the accepted values (${validOpportunitySourcesFormatted})`,
      rule: (e) => {
        if (e.value === '' || validOpportunitySources.includes(e.value)) {
          return true;
        } else {
          return false;
        }
      }
    }
  },
  { width: 90, field: 'rejectReason', displayName: 'Reject Reason', type: 'string', allowEditing: false },
  { width: 100, field: 'rejectedBy', displayName: 'Rejected By', type: 'string', allowEditing: false, visible: false },
  { width: 100, field: 'rejectedOn', displayName: 'Rejected On', type: 'datetime', allowEditing: false, visible: false },
  { width: 100, field: 'latitude', displayName: 'Latitude', type: 'number', allowEditing: false, visible: false },
  { width: 100, field: 'longitude', displayName: 'Longitude', type: 'number', allowEditing: false, visible: false },
  { width: 100, field: 'updatedBy', displayName: 'Updated By', type: 'string', allowEditing: false, visible: false },
  { width: 100, field: 'updatedOn', displayName: 'Updated On', type: 'datetime', allowEditing: false, visible: false },
  { width: 95, field: 'createdBy', displayName: 'Created By', type: 'string', allowEditing: false },
  { width: 125, field: 'createdOn', displayName: 'Created On', type: 'datetime', allowEditing: false, defaultSortIndex: 0, defaultSortOrder: 'desc' }
];

const lookupUrlMapper = {
  status: '/api/explore/prospectgrid/statusLookup',
  createdBy: '/api/explore/prospectgrid/createdUserLookup',
  rejectedBy: '/api/explore/prospectgrid/rejectedUserLookup',
  updatedBy: '/api/explore/prospectgrid/updatedUserLookup',
  locationVerified: '/api/explore/prospectgrid/booleanLookup',
  inspectionPeriod: '/api/explore/prospectgrid/booleanLookup',
}

const ProspectsGrid = React.memo(({
  classes,
  setModalSelectedRows,
  viewPropertyDetails,
  setIsRejectModalOpen,
  setIsCreateUnderwriteModalOpen,
  setGridLoading,
  setAddressToVerify,
  setOpenLocationVerification,
  prospectsGridRef,
  userHasSourcingManagerRole,
  userHasSourcingUserRole,
  user,
  setUnderwriteTxnOppId,
  setUnderwriteInfoModalOpen,
  appNotify,
  refreshProspectsGrid,
  loading,
}) => {
  // close column chooser filter after change location
  const location = useLocation();

  React.useEffect(() => {
    setIsColumnChooserVisible(false);
  }, [location]);

  const [dataSource, setDataSource] = useState(null);
  const [gridTotalCount, setGridTotalCount] = useState(0);
  const [isColumnChooserVisible, setIsColumnChooserVisible] = useState(false);

  useEffect(() => {
    createDataSource();
    // set loading for whole page only during first render or after reload data.
    // Turn off the loading in createDataSource method
    setGridLoading({ loading: true, message: 'Fetching prospects' });
  }, [refreshProspectsGrid]);

  function createDataSource() {
    let rows = [];
    const url = '/api/explore/prospectgrid';

    const store = new CustomStore({
      key: 'id',
      load: function(loadOptions) {
        if (isPlainObject(loadOptions.group)) {
          loadOptions.group = [loadOptions.group];
        }

        let params = {};

        dataGridExtension.customStoreRequestParams.forEach(i => {
          if (i in loadOptions && dataGridExtension.isNotEmpty(loadOptions[i])) {
            params[i]= loadOptions[i];
          }
        });

        return postWithToken(url, params)
          .then(response => response.json())
          .then((data) => {
            setGridTotalCount(data.absoluteTotalCount);
            rows = data.data;

            return {
              data: data.data,
              totalCount: data.totalCount,
            };
          })
          .catch((error) => {
            appNotify(`Failed to fetch prospects: ${error.message}`, 'error');
          })
          .finally(() => {
            setGridLoading({ loading: false, message: '' });
          })
      },
      update: function(key, values) {
        const payload = rows.find(row => row.id === key);
        return updateProspect({ ...payload, ...values }, user.id);
      }
    });

    setDataSource(store);
  }

  function onContentReady(grid) {
    // Set custom page count message
    const totalCount = grid.component.totalCount();
    const start = totalCount ? grid.component.pageIndex() * grid.component.pageSize() + 1 : 0;
    const end = totalCount ? start + grid.component.getVisibleRows().length - 1 : 0;
    const message = `Viewing ${start} - ${end} of ${totalCount.toLocaleString('en-US')} items`;
    const pageCount = grid.component.pageCount();
    const pageIndex = grid.component.pageIndex();
    const defaultMessage = `Page ${pageIndex + 1} of ${pageCount} (${totalCount.toLocaleString('en-US')} items)`;
    let groupingIsNotApplied = true;
    let columns = grid.component.getVisibleColumns();
    for (let i = 0; i < columns.length; i++) {
      if (columns[i].groupIndex !== undefined) {
        grid.component.option('pager', { infoText: defaultMessage });
        groupingIsNotApplied = false;
        break;
      }
    }
    if (groupingIsNotApplied) {
      grid.component.option('pager', { infoText: message });
    }

    let rows = grid.component.getVisibleRows();
    if (rows.length > 0) {
      rows.forEach(row => {
        let rowEl = grid.component.getRowElement(row.rowIndex);
        if (rowEl) {
          if (row.data.isProcessing) {
            if (rowEl[0]) {
              rowEl[0].classList.add('loadingRowColor');
            }
            if (rowEl[1]) {
              rowEl[1].classList.add('loadingRowColor');
              rowEl[1].classList.add('loadingRow');
            }
          } else {//Need this because of repaintChangesOnly=true
            if (rowEl[0] && row.data.isProcessing === false) {
              console.log('repainting remove row color')
              rowEl[0].classList.remove('loadingRowColor');
            }
            if (rowEl[1] && row.data.isProcessing === false) {
              console.log('repainting loadingrowcolor')
              rowEl[1].classList.remove('loadingRowColor');
              rowEl[1].classList.remove('loadingRow');
            }
          }
          if (rowEl[0].firstChild) {
            let rowEdit = rowEl[0].firstChild.children[0];
            // Once content is loaded, check the data to see if edit command column links need to be disabled (through css)
            if (row.data.status === 'Submitted' &&
              (
                (userHasSourcingUserRole && user.fullName === row.data.createdBy) ||
                userHasSourcingManagerRole
              )
            ) {
              if (rowEdit && rowEdit.classList && rowEdit.classList.contains('disableLink')) {
                rowEdit.classList.remove('disableLink');
              }
            } else {
              if (rowEdit && rowEdit.classList && !rowEdit.classList.contains('disableLink')) {
                rowEdit.classList.add('disableLink');
              }
            }
          }
        }
      });
    }
  }

  const onToolbarPreparing = e => {
    e.toolbarOptions.items.unshift(
      {
        location: 'before',
        template: 'excludedCount'
      },
    );
    e.toolbarOptions.items.unshift(
      {
        location: 'before',
        template: 'totalCount'
      },
    );
    // this adds a column chooser icon so we can customize its behaviour
    e.toolbarOptions.items.unshift({
      widget: "dxButton",
      location: "after",
      options: {
        icon: "columnchooser",
        elementAttr: {
          id: "prospectsGridColumnChooserBtn"
        },
        onClick: () => setIsColumnChooserVisible(true)
      }
    });
  }

  const toolbarItemRender = (e) => {
    switch (e.template) {
      case 'totalCount':
        return totalCountTemplate();
      case 'excludedCount':
        return exludedCountTemplate();
      default:
        return;
    }
  }

  const totalCountTemplate = (e) => {
    return (<div className={classes.informer}>
      <span className={classes.countLabel}>{gridTotalCount}</span>
      <span className={classes.totalCountText}>Total Count</span>
    </div>)
  }

  const exludedCountTemplate = (e) => {
    // NOTE: This is not working. Unsure why totalCount returns -1 and filter is null
    const totalCount = prospectsGridRef?.current?.instance?.totalCount();
    const filter = prospectsGridRef?.current?.instance?.getCombinedFilter();
    return totalCount && filter
      ? <div className={classes.exclusionInformer}>
        <span className={classes.countLabel}>{totalCount > -1 ? totalCount : 0}</span>
        <span className={classes.totalCountText}>Visible</span>
      </div>
      : null
  }

  function handleContextMenu(e) {
    if (e.target === 'content') {
      // e.items can be undefined
      if (!e.items) e.items = [];
      if (e.row) {
        const { rowType } = e.row;
        if (rowType === 'data') {
          const ref = prospectsGridRef.current.instance;
          if (!ref.isRowSelected(e.row.key)) {
            ref.selectRows([e.row.key]);
          }
          e.items = getProspectContextItems(e);
        } else if (rowType === 'group') {
          // e.items.push({
          //   text: 'Log group data to console',
          //   onItemClick: () => {
          //     console.log(e.row.data.items);
          //   }
          // });
        }
      }
    }
  }

  function getProspectContextItems(e) {
    const selectedRows = prospectsGridRef.current.instance.getSelectedRowsData();
    const maxReached = selectedRows.length > 100;
    const lengthText = maxReached ? 'max 100' : selectedRows.length;
    const cellValue = findNestedValue(e.row.data, e.column.dataField);
    const items = [];
    const selectedRowsSubmitted = !selectedRows.some(row => row.status !== 'Submitted');
    const selectedRowsLocationVerified = !selectedRows.some(row => !row.locationVerified);
    const selectedRowsHasIndicatives = !selectedRows.some(row => {
      if (!row.beds || !row.baths || !row.sqft || !row.yearBuilt || !row.listingPrice) {
        return true;
      } else {
        return false;
      }
    });
    // Check to see if any rows are currently creating underwrite
    const selectedRowsCurrentlyCreatingUnderwrite = selectedRows.some(row => {
      if (row.uwSubmissionStartedOn !== null && row.uwSubmissionFinishedOn == null) {
        return true;
      } else {
        return false;
      }
    });
    // Copy cell and row
    items.push({
      text: `Copy cell`,
      onItemClick: () => {
        copyToClipboard(cellValue);
      },
      beginGroup: true,
      disabled: selectedRows.length !== 1 || cellValue == null || e.row.data.isProcessing
    });
    items.push({
      text: `Copy row${selectedRows.length > 1 ? `s (${lengthText})` : ''}`,
      onItemClick: () => {
        const visibleColumns = prospectsGridRef.current.instance.getVisibleColumns().filter(col => col.dataField);
        const headerString = visibleColumns.map(col => col.caption).join('\t') + '\r\n';
        const combinedString = selectedRows.reduce((string, row) => {
          const rowValues = [];
          visibleColumns.forEach(col => {
            const value = findNestedValue(row, col.dataField);
            rowValues.push(value == null ? '\xa0' : value);
          });
          return string + rowValues.join('\t') + '\r\n';
        }, headerString);
        copyToClipboard(combinedString);
      },
      disabled: maxReached
    });

    // Filter by
    items.push({
      text: `Filter by selected (${selectedRows.length})`,
      onItemClick: () => {
        filterBySelected(selectedRows.map(opp => opp.id));
      },
      beginGroup: true
    });

    items.push({
      text: `View property details`,
      onItemClick: () => {
        viewPropertyDetails(e);
      },
      beginGroup: true,
      disabled: selectedRows.length !== 1 || e.row.data.isProcessing || e.row.data.asgPropID == null
    });
    items.push({
      text: 'View underwrite info',
      onItemClick: () => {
        viewUnderwriteInfo(e);
      },
      disabled: selectedRows.length !== 1 || e.row.data.isProcessing || selectedRowsSubmitted
    });

    items.push({
      text: `Verify location`,
      onItemClick: () => {
        verifyLocation(e);
      },
      beginGroup: true,
      disabled: selectedRows.length !== 1 || e.row.data.isProcessing || !userHasSourcingManagerRole || !selectedRowsSubmitted || selectedRowsCurrentlyCreatingUnderwrite
    });

    items.push({
      text: `Create underwrite (${selectedRows.length})`,
      onItemClick: () => {
        createUnderwrite(selectedRows);
      },
      disabled: e.row.data.isProcessing || !userHasSourcingManagerRole || !selectedRowsSubmitted || !selectedRowsLocationVerified || !selectedRowsHasIndicatives || selectedRowsCurrentlyCreatingUnderwrite
    });

    items.push({
      text: `Reject prospect (${selectedRows.length})`,
      onItemClick: () => {
        rejectProspect(selectedRows);
      },
      beginGroup: true,
      disabled: e.row.data.isProcessing || !userHasSourcingManagerRole || !selectedRowsSubmitted || selectedRowsCurrentlyCreatingUnderwrite
    });

    return items;
  }

  const createUnderwrite = rows => {
    setModalSelectedRows(rows);
    setIsCreateUnderwriteModalOpen(true);
  }

  const rejectProspect = (rows) => {
    setModalSelectedRows(rows);
    setIsRejectModalOpen(true);
  }

  const viewUnderwriteInfo = e => {
    if (e.row.data.transactionOpportunityId) {
      ReactDOM.unstable_batchedUpdates(() => {
        setUnderwriteTxnOppId(e.row.data.transactionOpportunityId);
        setModalSelectedRows([e.row.data]);
        setUnderwriteInfoModalOpen(true);
      });
    } else {
      appNotify('Unable to fetch underwrite info - Prospect has no transaction opportunity', 'warning');
    }
  }
  const verifyLocation = async (e) => {
    setGridLoading({ loading: true, message: 'Loading Location Verification' });
    setModalSelectedRows([e.row.data]);
    const locationDto = {
      referenceId: 0,
      street: e.row.data.streetAddress,
      zipCode: e.row.data.zipCode,
      city: e.row.data.city,
      state: e.row.data.state
    };
    try{
      const pmResult = await SearchPropMasterWithLocation(locationDto);
      setAddressToVerify({
        prospectID: e.row.data.id,
        street: e.row.data.streetAddress,
        city: e.row.data.city,
        state: e.row.data.state,
        zipCode: e.row.data.zipCode,
        latitude: e.row.data.latitude,
        longitude: e.row.data.longitude,
        locationVerified: e.row.data.locationVerified,
        geoResultDto: pmResult
      });
      setGridLoading({ loading: false, message: '' });
      setOpenLocationVerification(true);
    } catch(e){
      setGridLoading({ loading: false, message: '' });
    }
  }

  function findNestedValue(row, dataField) {
    if (!dataField) return undefined;
    let value = row;
    const split = dataField.split('.').filter(el => el !== '');

    for (const el of split) {
      if (value.hasOwnProperty(el)) {
        value = value[el];
      } else {
        value = undefined;
        break;
      }
    };
    return value;
  }

  function copyToClipboard(string) {
    const dummy = document.createElement('textarea');
    document.body.appendChild(dummy);
    dummy.value = string;
    dummy.select();
    document.execCommand('copy');
    document.body.removeChild(dummy);
  }

  function filterBySelected(ids) {
    const newState = { ...prospectsGridRef.current.instance.state() };
    newState.filterPanel.filterEnabled = true;
    newState.filterValue = ['id', 'anyof', ids];
    prospectsGridRef.current.instance.state(newState);
  }

  function getCellRender(cell) { // Render custom components for cells
    if (cell.value == null) {
      return null;
    }
    switch (cell.column.dataField) {
      case 'status':
        return <StatusChip status={cell.value} />
      case 'locationVerified':
      case 'inspectionPeriod':
        return <BoolIcon bool={cell.value} />
      case 'createdBy':
      case 'rejectedBy':
      case 'updatedBy':
        return <span>{cell.value}</span>
      default:
        return null;
    }
  }

  // solution from devextreme support team https://supportcenter.devexpress.com/ticket/details/t998890/datagrid-column-headerfilter-customization
  function calculateFilterExpression(
    filterValue,
    selectedFilterOperation,
    target
  ) {
    if (target === 'headerFilter') {
      return [this.dataField, selectedFilterOperation, filterValue];
    }
    return this.defaultCalculateFilterExpression.apply(this, arguments);
  }

  function getLookupHeaderDataSource(url) {
    return {
      store: new CustomStore({
        key: 'value',
        byKey: function(key) {
          return { id: key };
        },
        load: async function (loadOptions) {
          const { skip, take, searchValue, searchExpr, searchOperation } = loadOptions;
          const filter = dataGridExtension.getLookupFilter(loadOptions);

          const { data: grouped = [] } = await getWithToken(`${url}${filter}`)
            .then(response => response.json())
            .then((data) => ({ data: data.data }))
            .catch((error) => {
              appNotify(`Failed to fetch HeaderFilter data: ${error.message}`, 'error');
            });

          const blanksIndex = grouped.findIndex(
            item => dataGridExtension.checkIsBlanks(item.value)
          );
          if (blanksIndex > 0) {
            const blanksElement = grouped.splice(blanksIndex, 1)?.[0];
            grouped.splice(0, 0, blanksElement);
          }

          const mapped = grouped.map((item) => {
            const isBlanks = dataGridExtension.checkIsBlanks(item.value);

            const text = isBlanks ? '(Blanks)' : item.text;

            return {
              text,
              value: item.value,
            };
          });

          let query = Query(mapped);

          if (searchValue) {
            query = query.filter([searchExpr, searchOperation, searchValue]);
          }

          query = query.slice(skip, take);

          return query.toArray();
        }
      })
    }
  }

  let columns = useMemo(() => {
    return columnSchema.map(({
      field,
      displayName,
      width,
      type,
      allowEditing,
      defaultSortIndex,
      defaultSortOrder,
      validation,
      alignment,
      visible,
      format
    }) => {
      // original solutions for custom implementation from devextreme support team
// https://supportcenter.devexpress.com/ticket/details/t1014077/datagrid-header-filter-performance-for-large-client-side-dataset
// https://supportcenter.devexpress.com/ticket/details/t1004692/datagrid-searching-does-not-work-when-header-filter-date-values-are-ungrouped
      const headerDataSource = {
        store: new CustomStore({
          key: 'value',
          byKey: function(key) {
            return { id: key };
          },
          load: async function (loadOptions) {
            const { skip, take, searchValue, searchExpr, searchOperation } = loadOptions;

            const params = {
              group: [{ selector: field, isExpanded: false }],
            }

            const gridFilter = prospectsGridRef.current?.instance?.getCombinedFilter();
            // add filter param only for opening headerFilter
            if (gridFilter?.length && !loadOptions.filter) {
              // in headerFilter we need to show only values that consider grid filters
              params.filter = dataGridExtension.removeFilterValueForCurrentHF(gridFilter, field);
            }

            // for 'datetime', 'date' and 'number' headerFilters we use desc order
            if (['datetime', 'number', 'date'].includes(type)) {
              params.group[0].desc = true;
            }

            const { data: grouped = [] } = await postWithToken('/api/explore/prospectgrid', params)
              .then(response => response.json())
              .then((data) => ({ data: data.data }))
              .catch((error) => {
                appNotify(`Failed to fetch HeaderFilter data: ${error.message}`, 'error');
              });

            const blanksIndex = grouped.findIndex(
              item => dataGridExtension.checkIsBlanks(item.key)
            );
            if (blanksIndex > 0) {
              const blanksElement = grouped.splice(blanksIndex, 1)?.[0];
              grouped.splice(0, 0, blanksElement);
            }

            const mapped = grouped.map((item) => {
              const isBlanks = dataGridExtension.checkIsBlanks(item.key);
              // formatting can be different for all grids
              let formatOption;
              switch (format?.type) {
                case 'currency':
                  formatOption = {
                    style: format.type,
                    currency: 'USD',
                    maximumFractionDigits: format.precision
                  };
                  break;
                case 'fixedPoint':
                  formatOption = {
                    style: 'decimal',
                    minimumFractionDigits: format.precision,
                    maximumFractionDigits: format.precision
                  };
                  break;
              }

              let text;
              if (isBlanks) {
                text = '(Blanks)';
              } else {
                switch (type) {
                  case 'datetime':
                    text = new Date(item.key).toLocaleString();
                    break;
                  case 'date':
                    text = dateFormat(new Date(item.key), 'MM/dd/yyyy');
                    break;
                  case 'boolean':
                    text = item.key ? '✔' : '✖';
                    break;
                  case 'number':
                    if (['currency', 'fixedPoint'].includes(format?.type)) {
                      text = new Intl.NumberFormat('en-US', formatOption).format(item.key);
                      break;
                    }
                    text = item.key;
                    break;

                  default:
                    text = item.key;
                }
              }

              return {
                text,
                value: item.key,
              };
            });

            let query = Query(mapped);

            if (searchValue) {
              query = query.filter([searchExpr, searchOperation, searchValue]);
            }

            query = query.slice(skip, take);

            return query.toArray();
          }
        })
      }
      if (prospectsGridRef?.current?.instance) {
        let visibleCols = prospectsGridRef.current.instance.getVisibleColumns().map(({ dataField }) => dataField);
        visible = visibleCols.includes(field);
      }

      let cellRender = null;
      if ([
        'status',
        'locationVerified',
        'inspectionPeriod',
        'createdBy',
        'rejectedBy',
        'updatedBy',
      ].includes(field)) {
        cellRender = getCellRender;
      }

      if ([
        'createdBy',
        'status',
        'rejectedBy',
        'updatedBy',
        'locationVerified',
        'inspectionPeriod'
      ].includes(field)) {
        const lookupDataSource = getLookupHeaderDataSource(lookupUrlMapper[field], field);

        return (
          <Column
            key={field}
            dataField={field}
            caption={displayName}
            minWidth={width}
            dataType={type}
            allowEditing={allowEditing}
            defaultSortIndex={defaultSortIndex}
            defaultSortOrder={defaultSortOrder}
            alignment={alignment}
            cellRender={cellRender}
            visible={visible}
            format={format}
            lookup={{ displayExpr: 'text', valueExpr: 'value', dataSource: lookupDataSource }}
          >
            {validation &&
              <CustomRule
                message={validation.message}
                validationCallback={validation.rule}
              />
            }
            <HeaderFilter dataSource={lookupDataSource} groupInterval={null} />
          </Column>
        )
      }

      return (
        <Column
          key={field}
          dataField={field}
          caption={displayName}
          minWidth={width}
          dataType={type}
          allowEditing={allowEditing}
          defaultSortIndex={defaultSortIndex}
          defaultSortOrder={defaultSortOrder}
          alignment={alignment}
          cellRender={cellRender}
          visible={visible}
          format={format}
          calculateFilterExpression={calculateFilterExpression}
        >
          {validation &&
          <CustomRule
            message={validation.message}
            validationCallback={validation.rule}
          />
          }
          <HeaderFilter dataSource={headerDataSource} groupInterval={null} />
        </Column>
      )
    });
  }, [dataSource]);

  function changeLookupValueMap(data) {
    const columns = data?.component?.getVisibleColumns() || [];

    columns.forEach(column => {
      switch (column.dataField) {
        case 'status':
        case 'createdBy':
        case 'rejectedBy':
        case 'updatedBy':
        case 'locationVerified':
        case 'inspectionPeriod':
          if (column.lookup && column.lookup.valueMap) {
            const values = Object.values(column.lookup.valueMap);

            let newValues = {};
            values.forEach(value => newValues[value] = value);
            column.lookup.valueMap = {
              ...column.lookup.valueMap,
              ...newValues,
              null: '',
            };
          }
      }
    })
  }

  function onExporting(e) {
    changeLookupValueMap(e);
    const timestamp = dateFormat(new Date(), '_yyyy-MM-dd_HHmmss');
    const fileName = 'ReVUe_Prospects Export' + timestamp;
    dataGridExtension.exportToExcel(prospectsGridRef, fileName);
  }

  const sortFields = () => {
    let fields = [...columnSchema].map(c => ({ dataField: c.field, caption: c.displayName, dataType: c.type, format: c.format }));
    let getCaption = f => f.caption?.length > 0 ? f.caption.toLowerCase() : f.dataField.toLowerCase();
    fields.sort((a, b) => {
      if (getCaption(a) < getCaption(b))
        return -1;
      if (getCaption(a) > getCaption(b))
        return 1;
      return 0;
    });
    return fields;
  }

  return (
    <>
      <DataGrid
        id={'prospectsGrid'}
        ref={prospectsGridRef}
        dataSource={dataSource}
        allowColumnReordering={true}
        height={'calc(100vh - 200px)'}
        hoverStateEnabled={true}
        onToolbarPreparing={onToolbarPreparing}
        onContextMenuPreparing={handleContextMenu}
        loadPanel={{ enabled: loading === false }}
        onExporting={onExporting}
        repaintChangesOnly={true}
        highlightChanges={true}
        onContentReady={onContentReady}
        remoteOperations={true}
        enableTooltips
        tooltips={columnSchema}
      >
        <Editing mode={'row'} allowUpdating={true} />
        <Export enabled={true} allowExportSelectedData={true} />
        <ColumnFixing enable={true} />
        <FilterPanel visible={true} />
        <FilterBuilder fields={sortFields()}>
          {dataGridExtension.filterBuilderCustomOperations}
        </FilterBuilder>
        <FilterBuilderPopup />
        <HeaderFilter visible={true} search={{ enabled: true }} hideSelectAllOnSearch={false} />
        <Sorting mode={'multiple'} />
        <GroupPanel visible={true} />
        <SearchPanel visible={true} searchVisibleColumnsOnly={true} />
        <Grouping />
        <Selection mode={'multiple'} showCheckBoxesMode={false} />
        <Paging defaultPageSize={100} enabled={true} />
        <Pager showInfo={true} />
        <Template name='totalCount' render={toolbarItemRender} />
        <Template name='excludedCount' render={toolbarItemRender} />
        <Column type="buttons" name="actions" width={60} buttons={['edit']} showInColumnChooser={false} />
        {columns}
        <Column width='auto' name="default" showInColumnChooser={false} visibleIndex={9999} />
      </DataGrid>
      <CustomColumnChooser
        container="#prospectsGrid"
        visible={isColumnChooserVisible}
        onHiding={() => setIsColumnChooserVisible(false)}
        columns={columns}
        gridRef={prospectsGridRef}
      />
    </>
  )
});

export default withStyles(styles)(ProspectsGrid);
